import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import SignaturePad, { signatureImageFromFile } from './SignaturePad';

/**
 * Admin Settings → Contract Signing. The go-live switch for tour contract
 * e-signing (contractSettings/main) and the MST countersignature applied when
 * an admin clicks "Countersign" (contractSettings/signatory, admins only).
 */
export default function ContractSettings() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [enabled, setEnabled] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [saved, setSaved] = useState(null);
  const [name, setName] = useState('Michael McDonough');
  const [position, setPosition] = useState('Director');
  const [mode, setMode] = useState('draw'); // draw | upload
  const [image, setImage] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => onSnapshot(doc(db, 'contractSettings', 'main'), (s) => setEnabled(s.exists() && s.data().enabled === true)), []);
  useEffect(() => onSnapshot(doc(db, 'contractSettings', 'signatory'), (s) => {
    const d = s.exists() ? s.data() : null;
    setSaved(d);
    if (d) { setName(d.name || ''); setPosition(d.position || ''); }
  }), []);

  async function toggle(next) {
    setSavingToggle(true);
    try {
      await setDoc(doc(db, 'contractSettings', 'main'), { enabled: next, updatedAt: serverTimestamp(), updatedBy: currentUser.uid }, { merge: true });
    } catch (err) {
      console.error(err);
      toast('Could not change the setting', 'error');
    } finally {
      setSavingToggle(false);
    }
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError('');
    try { setImage(await signatureImageFromFile(file)); }
    catch (err) { setImage(null); setUploadError(err.message); }
  }

  const signatureToSave = image || saved?.signatureImage || null;
  const dirty = !!image || name.trim() !== (saved?.name || '') || position.trim() !== (saved?.position || '');

  async function save() {
    if (!name.trim() || !signatureToSave) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'contractSettings', 'signatory'), {
        name: name.trim(), position: position.trim(), signatureImage: signatureToSave,
        updatedAt: serverTimestamp(), updatedBy: currentUser.uid,
      });
      setImage(null);
      toast('Countersignature saved');
    } catch (err) {
      console.error(err);
      toast('Could not save the countersignature', 'error');
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">Contract Signing</h3>
        <p className="text-[12px] text-[#9CA3AF] mb-4">
          Tour contacts e-sign the Sports Tour Contract from a link, then an admin countersigns from the tour's Letters tab.
          While this is off, only admins can see and send contracts (for testing).
        </p>
        <div className="flex items-center justify-between gap-4 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] px-5 py-4">
          <div>
            <p className="text-[13px] font-bold text-[#111827]">Contract signing live for all staff</p>
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">
              {enabled ? 'On — staff can send contracts for signature' : 'Off — waiting for MST to approve the contract wording'}
            </p>
          </div>
          <button type="button" onClick={() => toggle(!enabled)} disabled={savingToggle} aria-pressed={enabled}
            className={`relative inline-flex h-[28px] w-[52px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-60 ${enabled ? 'bg-sg' : 'bg-[#D1D5DB]'}`}>
            <span className={`pointer-events-none inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-sm transition duration-200 ${enabled ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-1">MST Countersignature</h3>
        <p className="text-[12px] text-[#9CA3AF] mb-4">
          Added for MST Sports Tours when an admin clicks "Countersign". Only admins can see or change it.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <label className="block">
            <span className="block text-[12px] font-semibold text-[#374151] mb-1">Signatory name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className={inputCls} />
          </label>
          <label className="block">
            <span className="block text-[12px] font-semibold text-[#374151] mb-1">Position</span>
            <input value={position} onChange={(e) => setPosition(e.target.value)} maxLength={120} className={inputCls} />
          </label>
        </div>

        {saved?.signatureImage && !image && (
          <div className="mb-4">
            <p className="text-[12px] font-semibold text-[#374151] mb-1">Saved signature</p>
            <div className="h-[90px] rounded-[10px] border border-[#E5E7EB] bg-white flex items-center justify-center p-2">
              <img src={saved.signatureImage} alt="Saved countersignature" className="max-h-full max-w-full" />
            </div>
          </div>
        )}

        <p className="text-[12px] font-semibold text-[#374151] mb-1.5">{saved?.signatureImage ? 'Replace signature' : 'Add signature'}</p>
        <div className="flex gap-2 mb-3">
          {[['draw', 'Draw'], ['upload', 'Upload a scan']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => { setMode(id); setImage(null); setUploadError(''); }}
              className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold border transition ${
                mode === id ? 'bg-[#0D2240] text-white border-[#0D2240]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#0D2240]'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'draw' ? (
          <SignaturePad onChange={setImage} />
        ) : (
          <div>
            <input type="file" accept="image/png,image/jpeg" onChange={handleFile}
              className="block text-[13px] text-[#374151] file:mr-3 file:px-3 file:py-1.5 file:rounded-[8px] file:border file:border-[#E5E7EB] file:bg-white file:text-[12.5px] file:font-semibold" />
            <p className="text-[11.5px] text-[#9CA3AF] mt-1">Sign in dark ink on plain white paper and photograph or scan it. The white background is removed automatically.</p>
            {uploadError && <p className="text-[12.5px] text-red-600 mt-2">{uploadError}</p>}
            {image && (
              <div className="mt-3 h-[90px] rounded-[10px] border border-[#E5E7EB] bg-white flex items-center justify-center p-2">
                <img src={image} alt="New countersignature" className="max-h-full max-w-full" />
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button type="button" onClick={save} disabled={saving || !dirty || !name.trim() || !signatureToSave}
            className="px-5 py-2 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[13px] rounded-[8px] transition disabled:opacity-50">
            {saving ? 'Saving…' : 'Save countersignature'}
          </button>
        </div>
      </div>
    </div>
  );
}
